import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Media } from "@/components/Media";
import { toast } from "@/components/ui/sonner";
import { trackDemoClick } from "@/lib/telemetry";
import { MOBILE_TABS, MobileBottomNav, type MobileTabId } from "./MobileTabs";
import { Hero } from "@/components/Hero";
import { KeyBenefits } from "@/components/KeyBenefits";
import { HowItWorks } from "@/components/HowItWorks";
import { VideoSection } from "@/components/VideoSection";
import logoAsset from "@/assets/datasyteAI-logo-black.png.asset.json";

const PhotoMosaic = lazy(() => import("@/components/PhotoMosaic").then((m) => ({ default: m.PhotoMosaic })));
const TrainSection = lazy(() => import("@/components/TrainSection").then((m) => ({ default: m.TrainSection })));
const BenefitsGallery = lazy(() => import("@/components/BenefitsGallery").then((m) => ({ default: m.BenefitsGallery })));
const ProblemSection = lazy(() => import("@/components/ProblemSection").then((m) => ({ default: m.ProblemSection })));
const SolutionSection = lazy(() => import("@/components/SolutionSection").then((m) => ({ default: m.SolutionSection })));
const TechnologySection = lazy(() => import("@/components/TechnologySection").then((m) => ({ default: m.TechnologySection })));
const CompetitionSection = lazy(() => import("@/components/CompetitionSection").then((m) => ({ default: m.CompetitionSection })));
const MarketSection = lazy(() => import("@/components/MarketSection").then((m) => ({ default: m.MarketSection })));
const BusinessSection = lazy(() => import("@/components/BusinessSection").then((m) => ({ default: m.BusinessSection })));
const SecuritySection = lazy(() => import("@/components/SecuritySection").then((m) => ({ default: m.SecuritySection })));
const ComplianceSection = lazy(() => import("@/components/ComplianceSection").then((m) => ({ default: m.ComplianceSection })));
const ContactSection = lazy(() => import("@/components/ContactSection").then((m) => ({ default: m.ContactSection })));
const InvestButton = lazy(() => import("@/components/InvestButton").then((m) => ({ default: m.InvestButton })));
const FlipBookSection = lazy(() => import("@/components/FlipBookSection").then((m) => ({ default: m.FlipBookSection })));
const Footer = lazy(() => import("@/components/Footer").then((m) => ({ default: m.Footer })));

const Fallback = () => <div className="min-h-[30vh]" />;

const QuickTile = ({
  label,
  hint,
  onClick,
}: {
  label: string;
  hint: string;
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    className="flex min-h-[96px] flex-col justify-center rounded-2xl border border-border bg-card p-4 text-left shadow-sm active:scale-[0.98] transition-transform"
  >
    <span className="text-base font-bold text-foreground">{label}</span>
    <span className="mt-1 text-xs text-muted-foreground">{hint}</span>
  </button>
);

export const MobileApp = () => {
  const [tab, setTab] = useState<MobileTabId>("home");
  const scrollRef = useRef<HTMLDivElement>(null);
  const meta = MOBILE_TABS.find((t) => t.id === tab)!;

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0, behavior: "auto" });
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [tab]);

  useEffect(() => {
    const onBeforeInstall = (e: Event) => {
      e.preventDefault();
      (window as any).deferredInstallPrompt = e;
    };
    const onInstalled = () => {
      (window as any).deferredInstallPrompt = null;
      toast.success("AIMediQ™ installed!");
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 border-b border-border bg-background/95 backdrop-blur-md">
        <div className="flex items-center justify-between gap-3 px-4 py-3">
          <Media
            src={logoAsset.url}
            alt="datasyteAI™ by PathFree Technologies logo"
            className="h-9 w-auto object-contain"
            sizes="150px"
            eager
          />
          <Button asChild size="sm" className="min-h-11 rounded-full px-4">
            <a
              href="https://pathfree.com/contact"
              onClick={() => trackDemoClick("mobile_app_header")}
            >
              Book a Demo
            </a>
          </Button>
        </div>
        <div className="px-4 pb-3">
          <h1 className="text-xl font-extrabold leading-tight text-foreground">{meta.title}</h1>
          <p className="text-sm text-muted-foreground">{meta.subtitle}</p>
        </div>
      </header>

      <main ref={scrollRef} className="pb-28">
        {tab === "home" && (
          <>
            <Hero />
            <section className="px-4 py-8" aria-label="Quick links">
              <h2 className="mb-4 text-lg font-bold text-foreground">What do you want to see?</h2>
              <div className="grid grid-cols-2 gap-3">
                <QuickTile label="What is a Code Blue?" hint="Watch a short video" onClick={() => setTab("learn")} />
                <QuickTile label="How it works" hint="3 easy steps" onClick={() => setTab("learn")} />
                <QuickTile label="The smart cart" hint="See AIMediQ™" onClick={() => setTab("product")} />
                <QuickTile label="Invest with us" hint="Market & model" onClick={() => setTab("invest")} />
              </div>
            </section>
            <KeyBenefits />
          </>
        )}

        {tab === "learn" && (
          <Suspense fallback={<Fallback />}>
            <VideoSection />
            <HowItWorks />
            <ProblemSection />
            <PhotoMosaic />
          </Suspense>
        )}

        {tab === "product" && (
          <Suspense fallback={<Fallback />}>
            <SolutionSection />
            <TechnologySection />
            <TrainSection />
            <BenefitsGallery />
            <SecuritySection />
            <ComplianceSection />
          </Suspense>
        )}

        {tab === "invest" && (
          <Suspense fallback={<Fallback />}>
            <MarketSection />
            <BusinessSection />
            <CompetitionSection />
            <InvestButton />
            <section className="px-4 pb-4" aria-label="More investor information">
              <a
                href="https://pathfree.com/investors"
                className="flex min-h-14 items-center justify-between rounded-2xl border border-border bg-card px-4 font-semibold text-foreground shadow-sm active:scale-[0.98] transition-transform"
              >
                <span>More information</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-muted-foreground"
                  aria-hidden="true"
                >
                  <path d="M7 7h10v10" />
                  <path d="M7 17 17 7" />
                </svg>
              </a>
            </section>
            <FlipBookSection />
          </Suspense>
        )}

        {tab === "contact" && (
          <Suspense fallback={<Fallback />}>
            <ContactSection />
            <section className="px-4 pb-8" aria-label="Legal">
              <h2 className="mb-3 text-lg font-bold text-foreground">The fine print</h2>
              <div className="grid gap-3">
                <Link
                  to="/disclaimers"
                  className="flex min-h-14 items-center rounded-2xl border border-border bg-card px-4 font-semibold text-foreground"
                >
                  Disclaimers
                </Link>
                <Link
                  to="/privacy"
                  className="flex min-h-14 items-center rounded-2xl border border-border bg-card px-4 font-semibold text-foreground"
                >
                  Privacy Policy
                </Link>
                <Link
                  to="/terms"
                  className="flex min-h-14 items-center rounded-2xl border border-border bg-card px-4 font-semibold text-foreground"
                >
                  Terms of Service
                </Link>
              </div>
            </section>
            <Footer />
          </Suspense>
        )}
      </main>

      <MobileBottomNav active={tab} onChange={setTab} onInstall={handleInstall} />
    </div>
  );
};

const handleInstall = async () => {
  const prompt = (window as any).deferredInstallPrompt;
  if (prompt) {
    await prompt.prompt();
    const { outcome } = await prompt.userChoice;
    if (outcome === "accepted") {
      toast.success("Installing AIMediQ™...");
    }
    (window as any).deferredInstallPrompt = null;
  } else {
    toast("Install this app", {
      description: "Tap your browser menu (or Share on iOS) and choose 'Add to Home Screen'.",
      duration: 6000,
    });
  }
};
